import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import authAPI from "../../../services/authApi";
import clinicAPI from "../../../services/clinicApi";
import { hasAccessToken } from "../../../utils/authStorage";
import { getClinicPortalLinks } from "../../../utils/clinicPortalLinks";
import {
  firstFieldError,
  validateTrainerInviteForm,
} from "../../../utils/trainerFormValidation";
import TrainerAssignmentsFields, { toggleId } from "../AdminTeam/TrainerAssignmentsFields";
import { DOCTORS_BASE } from "./doctorConstants";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  Field,
  Input,
  PhoneInput,
  Textarea,
  FormSection,
  FormGrid,
  useToast,
} from "../../ui";

export default function AdminDoctorNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const { tenant } = useSelector((state) => state.auth);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    fullName: "",
    description: "",
    phone: "",
    email: "",
    password: "",
    groupIds: [],
  });

  const staffPortalUrl =
    getClinicPortalLinks(tenant?.subdomain).find((l) => l.id === "staff")?.url ||
    "";

  useEffect(() => {
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setGroups((r.specializations || []).filter((x) => x.is_active !== false)),
      )
      .catch(() => setGroups([]));
  }, []);

  const patch = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAccessToken()) {
      toast.error("Сессия истекла. Войдите снова.");
      return;
    }
    const errors = validateTrainerInviteForm(form, { requirePassword: true });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      toast.error(firstFieldError(errors));
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      const res = await authAPI.inviteTrainer({
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        fullName: form.fullName.trim(),
        description: form.description?.trim() || undefined,
        groupIds: form.groupIds,
      });
      if (res?.result !== 0) {
        toast.error(res?.error || "Не удалось добавить тренера");
        return;
      }
      toast.success(
        `Тренер «${res.member?.full_name || res.trainer?.full_name}» добавлен${staffPortalUrl ? ` · ${staffPortalUrl}` : ""}`,
        7000,
      );
      const tid = res.member?.trainer_id || res.trainer?.id || res.doctor?.id;
      if (tid) {
        navigate(`${DOCTORS_BASE}/${tid}`);
      } else {
        navigate(DOCTORS_BASE);
      }
    } catch (e) {
      const msg = e.response?.data?.error || "Ошибка";
      toast.error(msg);
      if (/телефон/i.test(msg)) setFieldErrors((prev) => ({ ...prev, phone: msg }));
      else if (/email/i.test(msg)) setFieldErrors((prev) => ({ ...prev, email: msg }));
      else if (/парол/i.test(msg)) setFieldErrors((prev) => ({ ...prev, password: msg }));
      else if (/фио|имя/i.test(msg)) setFieldErrors((prev) => ({ ...prev, fullName: msg }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.patientPage}>
      <nav className={`${s.patientBreadcrumbs} ${s.patientPageBlock}`}>
        <Link to="/manage/app/team">Команда</Link>
        <span aria-hidden>/</span>
        <span>Новый тренер</span>
      </nav>

      <Card
        title="Добавить тренера"
        subtitle="Назначьте группы — тренер увидит их занятия в расписании и журнале"
      >
        <form onSubmit={submit}>
          <FormSection title="Данные тренера">
            <FormGrid>
              <Field label="ФИО *" error={fieldErrors.fullName}>
                <Input
                  required
                  value={form.fullName}
                  error={fieldErrors.fullName}
                  onChange={(e) => patch("fullName", e.target.value)}
                />
              </Field>
              <Field label="Описание">
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => patch("description", e.target.value)}
                />
              </Field>
            </FormGrid>
          </FormSection>

          <FormSection title="Доступ в портал">
            <FormGrid>
              <Field
                label="Email *"
                hint="Основной идентификатор для входа в портал"
                error={fieldErrors.email}
              >
                <Input
                  type="email"
                  required
                  value={form.email}
                  error={fieldErrors.email}
                  onChange={(e) => patch("email", e.target.value)}
                  placeholder="email@example.com"
                />
              </Field>
              <Field
                label="Телефон"
                hint="Необязательно — для звонков и SMS"
                error={fieldErrors.phone}
              >
                <PhoneInput
                  value={form.phone}
                  error={fieldErrors.phone}
                  onChange={(v) => patch("phone", v)}
                />
              </Field>
              <Field label="Пароль *" hint="Минимум 8 символов" error={fieldErrors.password}>
                <Input
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  error={fieldErrors.password}
                  onChange={(e) => patch("password", e.target.value)}
                />
              </Field>
            </FormGrid>
          </FormSection>

          <FormSection
            title="Группы"
            description="Можно выбрать несколько — тренер ведёт все отмеченные группы"
          >
            <TrainerAssignmentsFields
              groupsOnly
              optional
              groups={groups}
              groupIds={form.groupIds}
              onToggleGroup={(id) =>
                setForm((f) => ({
                  ...f,
                  groupIds: toggleId(f.groupIds, id),
                }))
              }
            />
          </FormSection>

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" disabled={loading}>
              {loading ? "Создание…" : "Пригласить тренера"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(DOCTORS_BASE)}
            >
              Отмена
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
